import groupBy from "lodash/groupBy";
import sumBy from "lodash/sumBy";
import sortBy from "lodash/sortBy";

import {
  MaterialCategoryName,
  MaterialGroup,
  MaterialGroupByRegYear,
  MaterialUnit,
} from "fe-shared/src/types/domain";

import { TransactionTraversedMaterial } from "../types";

const DIGITS_AFTER_POINT = 7;

const sumMaterialsByCode = (
  materials: TransactionTraversedMaterial[]
): TransactionTraversedMaterial[] => {
  const groupedMaterials = groupBy(materials, "materialCode");

  return (Object.values(groupedMaterials) as TransactionTraversedMaterial[][])
    .map((g: TransactionTraversedMaterial[]) => ({
      ...g[0],
      units: {
        unit: g[0].units.unit,
        unitQuantity: Math.floor(
          Number.parseFloat(sumBy(g, (i: TransactionTraversedMaterial) => i.units.unitQuantity).toFixed(DIGITS_AFTER_POINT))
        ),
        weight: Math.floor(
          Number.parseFloat(sumBy(g, (i: TransactionTraversedMaterial) => i.units.weight || 0).toFixed(DIGITS_AFTER_POINT))
        ),
      },
    }))
    .flat();
};

const getMaterialsGroup = (
  materials: TransactionTraversedMaterial[],
  materialCategoryName: MaterialCategoryName
): MaterialGroup<TransactionTraversedMaterial> => {
  const items = sortBy(
    sumMaterialsByCode(
      materials.filter(
        (m) =>
          m.materialCategoryName === materialCategoryName &&
          m.units.unitQuantity
      )
    ),
    [(o: TransactionTraversedMaterial) => o.materialId]
  ) as TransactionTraversedMaterial[];

  return {
    items,
    total: Math.floor(sumBy(items, (i: TransactionTraversedMaterial) => i.units.unitQuantity)),
    weight: Math.floor(sumBy(items, (i: TransactionTraversedMaterial) => i.units.weight || 0)),
    materialCategoryName,
    unit: items[0]?.units.unit || MaterialUnit.Kg,
    regYear: items[0]?.regYear,
  };
};

const groupMaterialsByType = (
  currentMaterials: TransactionTraversedMaterial[]
) => {
  return [
    getMaterialsGroup(currentMaterials, MaterialCategoryName.Tires),
    getMaterialsGroup(currentMaterials, MaterialCategoryName.Products),
  ].filter((g) => g.items.length);
};

const getMaterialGroupsByRegYear = (
  traversedMaterials?: TransactionTraversedMaterial[] | null
): MaterialGroupByRegYear<TransactionTraversedMaterial>[] => {
  if (!traversedMaterials) return [];

  const groupedTraversedMaterials = groupBy(traversedMaterials, "regYear");
  const materialGroupsByRegYear: MaterialGroupByRegYear<TransactionTraversedMaterial>[] =
    [];

  (Object.entries(groupedTraversedMaterials) as [string, TransactionTraversedMaterial[]][]).forEach((entry) => {
    const [regYear, materials] = entry;
    const materialGroups = groupMaterialsByType(materials);

    materialGroupsByRegYear.push({
      regYear: regYear === "null" ? null : Number(regYear),
      weight: Number.parseFloat(
        sumBy(materialGroups, (i: MaterialGroup<TransactionTraversedMaterial>) => i.weight || 0).toFixed(DIGITS_AFTER_POINT)
      ),
      materialGroups,
    });
  });

  return materialGroupsByRegYear;
};

export default getMaterialGroupsByRegYear;
